import React, { useRef, useState, useCallback, useEffect } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Button, Tooltip } from 'antd';
import {
  EditOutlined,
  BorderOutlined,
  FormOutlined,
  ExpandOutlined,
  RotateLeftOutlined,
  RotateRightOutlined,
  CheckOutlined,
  CloseOutlined,
  CloseCircleOutlined,
  SwitcherOutlined,
  SwapOutlined,
  ColumnHeightOutlined,
} from '@ant-design/icons';

function ImageCropper({ src, initialCrop, onComplete, onCancel }) {
  // Если initialCrop не передан, выбираем всю картинку по умолчанию (в процентах)
  const [crop, setCrop] = useState(
    initialCrop || { unit: '%', x: 0, y: 0, width: 100, height: 100 }
  );
  const [completedCrop, setCompletedCrop] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  // imageScale больше не используется для расчёта; оставлено для совместимости, но не хранится
  const imageRef = useRef(null);
  const [displayedSrc, setDisplayedSrc] = useState(src);
  const [imgKey, setImgKey] = useState(0);
  const [invertColors, setInvertColors] = useState(false);

  // Ключ для отслеживания загрузок изображения (для различения нового и повторного выбора)
  const [imageLoadKey, setImageLoadKey] = useState(0);
  const prevSrcRef = useRef(src);

  // Сохранение состояния рисования для восстановления при повторной загрузке
  const [savedDrawingData, setSavedDrawingData] = useState(null);

  // Единые стили для круглых кнопок с иконками (центрируем и нормализуем размер)
  const smallCircleBtnStyle = { width: 28, height: 28, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' };
  const largeCircleBtnStyle = { width: 40, height: 40, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' };
  const smallIconStyle = { fontSize: 16, lineHeight: 1 };
  const largeIconStyle = { fontSize: 18, lineHeight: 1 };

  // Полотно для рисования (натуральный размер изображения, масштабируется стилями)
  const overlayCanvasRef = useRef(null);
  const containerRef = useRef(null);
  // Кэш исходного изображения в canvas (натуральный размер) для пипетки
  const baseCanvasRef = useRef(null);
  // Коэффициенты перевода: дисплейные пиксели оверлея -> натуральные пиксели изображения
  const overlayToNaturalScaleRef = useRef({ sx: 1, sy: 1 });

  // Инструменты рисования
  const [activeTool, setActiveTool] = useState('none'); // 'none' | 'pencil' | 'eraser' | 'eyedropper'
  const [brushColor, setBrushColor] = useState('#ff0000');
  const [brushSize, setBrushSize] = useState(5);
  const isDrawingRef = useRef(false);
  const lastPointRef = useRef({ x: 0, y: 0 });

  // Состояние для курсора кисти
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });
  const [showBrushCursor, setShowBrushCursor] = useState(false);

  // Функция для обновления базового canvas с учётом инверсии
  const updateBaseCanvas = useCallback(() => {
    const img = imageRef.current;
    if (!img || !img.complete || !img.naturalWidth || !img.naturalHeight) return;

    try {
      if (!baseCanvasRef.current) baseCanvasRef.current = document.createElement('canvas');
      const base = baseCanvasRef.current;
      base.width = img.naturalWidth;
      base.height = img.naturalHeight;
      const bctx = base.getContext('2d');
      if (!bctx) return;

      bctx.clearRect(0, 0, base.width, base.height);

      // Применяем инверсию, если она включена
      if (invertColors) {
        bctx.filter = 'invert(1)';
      }

      bctx.drawImage(img, 0, 0, base.width, base.height);

      // Сбрасываем фильтр после рисования
      bctx.filter = 'none';
    } catch (e) {
      // no-op
    }
  }, [invertColors]);

  const onImageLoaded = useCallback((img) => {
    // console.log('onImageLoaded called:', img.naturalWidth, 'x', img.naturalHeight);
    imageRef.current = img;
    setImageLoaded(true);

    // Инициализируем базовый canvas для пипетки
    updateBaseCanvas();
    return false; // отключаем автоматическую коррекцию crop
  }, [updateBaseCanvas]);

  const handleImageLoad = useCallback((e) => {
    const img = e.target;
    imageRef.current = img;
    setImageLoaded(true);
  }, []);

  // Синхронизация положения/размера оверлея с фактическими размерами IMG
  const syncOverlayPosition = useCallback(() => {
    const container = containerRef.current;
    const img = imageRef.current;
    const overlay = overlayCanvasRef.current;
    if (!container || !img || !overlay) return;

    // Проверяем, что изображение действительно загружено
    if (!img.complete || !img.naturalWidth || !img.naturalHeight) return;

    const parentRect = container.getBoundingClientRect();
    const imgRect = img.getBoundingClientRect();
    const left = imgRect.left - parentRect.left;
    const top = imgRect.top - parentRect.top;
    overlay.style.left = `${left}px`;
    overlay.style.top = `${top}px`;
    // Обновляем CSS размеры
    overlay.style.width = `${imgRect.width}px`;
    overlay.style.height = `${imgRect.height}px`;
    // И актуализируем внутреннее разрешение, коэффициенты перевода и очищаем слой
    const dW = Math.max(1, Math.round(imgRect.width));
    const dH = Math.max(1, Math.round(imgRect.height));
    if (overlay.width !== dW || overlay.height !== dH) {
      overlay.width = dW;
      overlay.height = dH;
      overlayToNaturalScaleRef.current = {
        sx: img.naturalWidth / dW,
        sy: img.naturalHeight / dH,
      };
      const octx = overlay.getContext('2d');
      octx && octx.clearRect(0, 0, overlay.width, overlay.height);
    }
  }, []);

  useEffect(() => {
    syncOverlayPosition();
    const onResize = () => syncOverlayPosition();
    const onScroll = () => syncOverlayPosition();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    let ro;
    if (imageRef.current && 'ResizeObserver' in window) {
      ro = new ResizeObserver(() => syncOverlayPosition());
      ro.observe(imageRef.current);
    }
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
      if (ro) ro.disconnect();
    };
  }, [displayedSrc, imageLoaded, syncOverlayPosition, imageLoadKey]);

  // Функция для сохранения текущего состояния рисования
  const saveDrawingState = useCallback(() => {
    const overlay = overlayCanvasRef.current;
    if (!overlay) return;

    try {
      const ctx = overlay.getContext('2d');
      if (ctx && overlay.width && overlay.height) {
        // Сохраняем данные canvas
        const imageData = ctx.getImageData(0, 0, overlay.width, overlay.height);
        setSavedDrawingData({
          data: imageData,
          width: overlay.width,
          height: overlay.height
        });
      }
    } catch (e) {
      console.warn('Не удалось сохранить состояние рисования:', e);
    }
  }, []);

  // Функция для восстановления состояния рисования
  const restoreDrawingState = useCallback(() => {
    const overlay = overlayCanvasRef.current;
    if (!overlay || !savedDrawingData) return;

    try {
      const ctx = overlay.getContext('2d');
      if (ctx && savedDrawingData.data) {
        // Восстанавливаем данные на canvas
        ctx.putImageData(savedDrawingData.data, 0, 0);
      }
    } catch (e) {
      console.warn('Не удалось восстановить состояние рисования:', e);
    }
  }, [savedDrawingData]);

  // Дополнительная инициализация canvas после загрузки изображения (один эффект; clearTimeout при размонтировании)
  useEffect(() => {
    if (!imageLoaded || !imageRef.current) return undefined;
    const t = window.setTimeout(() => {
      syncOverlayPosition();
      if (savedDrawingData) {
        restoreDrawingState();
      }
    }, 10);
    return () => clearTimeout(t);
  }, [imageLoaded, syncOverlayPosition, imageLoadKey, savedDrawingData, restoreDrawingState]);

  // Обновляем базовый canvas при изменении инверсии
  useEffect(() => {
    if (imageLoaded && imageRef.current) {
      updateBaseCanvas();
    }
  }, [invertColors, imageLoaded, updateBaseCanvas, imageLoadKey]);

  // Если меняется источник изображения снаружи — сбрасываем выбор на весь кадр и чистим рисование
  useEffect(() => {
    const isNewImage = src !== prevSrcRef.current;

    if (isNewImage) {
      // Новое изображение - сбрасываем ВСЕ состояния
      setDisplayedSrc(src || '');
      setImgKey(k => k + 1); // форсируем размонтирование/монтаж <img>, чтобы гарантировать onLoad
      setCrop(initialCrop || { unit: '%', x: 0, y: 0, width: 100, height: 100 });
      setCompletedCrop(null);
      setImageLoaded(false);
      setInvertColors(false);
      setActiveTool('none');
      setBrushColor('#ff0000');
      setBrushSize(5);
      setImageLoadKey(k => k + 1); // увеличиваем ключ загрузки
      setSavedDrawingData(null); // очищаем сохраненные данные рисования
      // очистить холст рисования
      const overlay = overlayCanvasRef.current;
      if (overlay) {
        const octx = overlay.getContext('2d');
        octx && octx.clearRect(0, 0, overlay.width || 0, overlay.height || 0);
      }
    } else {
      // То же изображение выбрано повторно - сохраняем все текущие состояния
      saveDrawingState(); // сохраняем текущее состояние рисования
      setDisplayedSrc(src || '');
      setImgKey(k => k + 1); // форсируем размонтирование/монтаж <img>, чтобы гарантировать onLoad
      setImageLoaded(false); // только флаг загрузки сбрасываем
      setImageLoadKey(k => k + 1); // увеличиваем ключ загрузки для перезагрузки
    }

    prevSrcRef.current = src; // обновляем предыдущий src
  }, [src, initialCrop, setActiveTool, setBrushColor, setBrushSize, saveDrawingState]);

  // Утилита: перевод координат курсора в натуральные пиксели
  const getNaturalCoords = useCallback((evt) => {
    // Используем размеры самого overlay canvas: координаты в дисплейных пикселях оверлея
    const overlay = overlayCanvasRef.current;
    if (!overlay) return { x: 0, y: 0 };
    const rect = overlay.getBoundingClientRect();
    const xDisplay = Math.max(0, Math.min(evt.clientX - rect.left, rect.width));
    const yDisplay = Math.max(0, Math.min(evt.clientY - rect.top, rect.height));
    const x = Math.round((xDisplay / rect.width) * overlay.width);
    const y = Math.round((yDisplay / rect.height) * overlay.height);
    return { x, y };
  }, []);

  const handlePointerDown = useCallback((evt) => {
    if (activeTool === 'none') return; // даём работать выделению
    evt.preventDefault();
    evt.stopPropagation();
    const overlay = overlayCanvasRef.current;
    if (!overlay) return;
    const ctx = overlay.getContext('2d');
    if (!ctx) return;
    const { x, y } = getNaturalCoords(evt);
    if (activeTool === 'eyedropper') {
      // Считываем цвет из самого изображения (натуральные координаты)
      const sampleFromBase = () => {
        const img = imageRef.current;
        const base = baseCanvasRef.current;
        if (!img) return null;
        try {
          if (base) {
            const bctx = base.getContext('2d');
            if (!bctx) return null;
            const sx = Math.min(Math.max(0, Math.round(x * overlayToNaturalScaleRef.current.sx)), img.naturalWidth - 1);
            const sy = Math.min(Math.max(0, Math.round(y * overlayToNaturalScaleRef.current.sy)), img.naturalHeight - 1);
            const data = bctx.getImageData(sx, sy, 1, 1).data;
            return `#${[data[0], data[1], data[2]].map(v => v.toString(16).padStart(2, '0')).join('')}`;
          }
        } catch (e) {
          // возможно таинт из-за CORS — попробуем временный канвас
        }
        try {
          const tmp = document.createElement('canvas');
          tmp.width = 1; tmp.height = 1;
          const tctx = tmp.getContext('2d');
          if (!tctx) return null;
          const sx = Math.min(Math.max(0, Math.round(x * overlayToNaturalScaleRef.current.sx)), img.naturalWidth - 1);
          const sy = Math.min(Math.max(0, Math.round(y * overlayToNaturalScaleRef.current.sy)), img.naturalHeight - 1);
          tctx.drawImage(img, sx, sy, 1, 1, 0, 0, 1, 1);
          const data = tctx.getImageData(0, 0, 1, 1).data;
          return `#${[data[0], data[1], data[2]].map(v => v.toString(16).padStart(2, '0')).join('')}`;
        } catch (e) {
          return null;
        }
      };
      const picked = sampleFromBase();
      if (picked) setBrushColor(picked);
      return;
    }
    isDrawingRef.current = true;
    lastPointRef.current = { x, y };
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    // Толщина в натуральных пикселях canvas оверлея
    ctx.lineWidth = Math.max(1, brushSize);
    if (activeTool === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.strokeStyle = 'rgba(0,0,0,1)';
    } else {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = brushColor;
    }
    ctx.beginPath();
    ctx.moveTo(x, y);
  }, [activeTool, brushColor, brushSize, getNaturalCoords]);

  const handlePointerMove = useCallback((evt) => {
    // Обновляем позицию курсора для показа круга
    if (activeTool === 'pencil' || activeTool === 'eraser') {
      const rect = evt.currentTarget.getBoundingClientRect();
      setCursorPos({
        x: evt.clientX - rect.left,
        y: evt.clientY - rect.top
      });
      setShowBrushCursor(true);
    } else {
      setShowBrushCursor(false);
    }

    if (activeTool === 'none') return;
    evt.preventDefault();
    evt.stopPropagation();
    if (!isDrawingRef.current) return;
    const overlay = overlayCanvasRef.current;
    if (!overlay) return;
    const ctx = overlay.getContext('2d');
    if (!ctx) return;
    const { x, y } = getNaturalCoords(evt);
    ctx.lineTo(x, y);
    ctx.stroke();
    lastPointRef.current = { x, y };
  }, [getNaturalCoords, activeTool]);

  const handlePointerUp = useCallback((evt) => {
    if (activeTool === 'none') return;
    evt.preventDefault();
    evt.stopPropagation();
    if (!isDrawingRef.current) return;
    isDrawingRef.current = false;
  }, [activeTool]);

  const handlePointerLeave = useCallback(() => {
    setShowBrushCursor(false);
  }, []);

  const handlePointerEnter = useCallback((evt) => {
    if (activeTool === 'pencil' || activeTool === 'eraser') {
      setShowBrushCursor(true);
    }
  }, [activeTool]);

  const clearDrawing = useCallback(() => {
    const overlay = overlayCanvasRef.current;
    if (!overlay) return;
    const ctx = overlay.getContext('2d');
    ctx && ctx.clearRect(0, 0, overlay.width, overlay.height);
    setSavedDrawingData(null); // очищаем сохраненные данные рисования
  }, []);

  const rotateDisplayed = useCallback((direction) => {
    // direction: 'left' | 'right'
    const img = imageRef.current;
    if (!img || !img.naturalWidth || !img.naturalHeight) return;
    const rotateRight = direction === 'right';
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.width = h;
    canvas.height = w;
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((rotateRight ? 90 : -90) * Math.PI / 180);
    ctx.drawImage(img, -w / 2, -h / 2);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
    // Обновим источник и сбросим состояния
    setDisplayedSrc(dataUrl);
    setCrop({ unit: '%', x: 0, y: 0, width: 100, height: 100 });
    setCompletedCrop(null);
    setImageLoaded(false);
    clearDrawing();
  }, [clearDrawing]);

  // Обновленная функция поворота с сохранением рисования
  const rotateDisplayedWithDrawing = useCallback((direction) => {
    saveDrawingState(); // сохраняем текущее состояние рисования
    rotateDisplayed(direction);
  }, [rotateDisplayed, saveDrawingState]);

  const flipHorizontal = useCallback(() => {
    const img = imageRef.current;
    if (!img || !img.naturalWidth || !img.naturalHeight) return;
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.width = w;
    canvas.height = h;
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.scale(-1, 1);
    ctx.drawImage(img, -w / 2, -h / 2);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
    // Обновим источник и сбросим состояния
    setDisplayedSrc(dataUrl);
    setCrop({ unit: '%', x: 0, y: 0, width: 100, height: 100 });
    setCompletedCrop(null);
    setImageLoaded(false);
    clearDrawing();
  }, [clearDrawing]);

  const flipVertical = useCallback(() => {
    const img = imageRef.current;
    if (!img || !img.naturalWidth || !img.naturalHeight) return;
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.width = w;
    canvas.height = h;
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.scale(1, -1);
    ctx.drawImage(img, -w / 2, -h / 2);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
    // Обновим источник и сбросим состояния
    setDisplayedSrc(dataUrl);
    setCrop({ unit: '%', x: 0, y: 0, width: 100, height: 100 });
    setCompletedCrop(null);
    setImageLoaded(false);
    clearDrawing();
  }, [clearDrawing]);

  // Обновленные функции зеркального отражения с сохранением рисования
  const flipHorizontalWithDrawing = useCallback(() => {
    saveDrawingState(); // сохраняем текущее состояние рисования
    flipHorizontal();
  }, [flipHorizontal, saveDrawingState]);

  const flipVerticalWithDrawing = useCallback(() => {
    saveDrawingState(); // сохраняем текущее состояние рисования
    flipVertical();
  }, [flipVertical, saveDrawingState]);

  // Функция для преобразования crop из отображаемых координат в реальные пиксели
  const getPixelCrop = useCallback((cropObj, image) => {
    if (!image || !image.naturalWidth || !image.naturalHeight) {
      console.error('Image not properly loaded:', image);
      return null;
    }

    let pixelCrop;
    const rect = image.getBoundingClientRect();
    const currentScaleX = image.naturalWidth / rect.width;
    const currentScaleY = image.naturalHeight / rect.height;
    
    if (cropObj.unit === '%') {
      // Если crop в процентах, сначала переводим в пиксели отображаемого размера
      const displayCrop = {
        x: (cropObj.x * rect.width) / 100,
        y: (cropObj.y * rect.height) / 100,
        width: (cropObj.width * rect.width) / 100,
        height: (cropObj.height * rect.height) / 100,
      };
      
      // Затем масштабируем к реальным размерам
      pixelCrop = {
        x: Math.round(displayCrop.x * currentScaleX),
        y: Math.round(displayCrop.y * currentScaleY),
        width: Math.round(displayCrop.width * currentScaleX),
        height: Math.round(displayCrop.height * currentScaleY),
      };
    } else {
      // Если crop уже в пикселях отображаемого размера
      pixelCrop = {
        x: Math.round(cropObj.x * currentScaleX),
        y: Math.round(cropObj.y * currentScaleY),
        width: Math.round(cropObj.width * currentScaleX),
        height: Math.round(cropObj.height * currentScaleY),
      };
    }

    // Проверяем границы изображения
    pixelCrop.x = Math.max(0, Math.min(pixelCrop.x, image.naturalWidth));
    pixelCrop.y = Math.max(0, Math.min(pixelCrop.y, image.naturalHeight));
    pixelCrop.width = Math.max(1, Math.min(pixelCrop.width, image.naturalWidth - pixelCrop.x));
    pixelCrop.height = Math.max(1, Math.min(pixelCrop.height, image.naturalHeight - pixelCrop.y));

    return pixelCrop;
  }, []);

  const generateCroppedImage = useCallback(async () => {
    if (!imageLoaded || !imageRef.current) {
      console.error('Image not loaded yet');
      return null;
    }

    const imgElement = imageRef.current;
    const currentCrop = completedCrop || crop;
    
    if (!currentCrop || !currentCrop.width || !currentCrop.height) {
      console.error('Invalid crop dimensions:', currentCrop);
      return null;
    }

    // Преобразуем crop в пиксели
    const pixelCrop = getPixelCrop(currentCrop, imgElement);
    if (!pixelCrop) {
      console.error('Failed to get pixel crop');
      return null;
    }


    const canvas = document.createElement('canvas');
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;
    const ctx = canvas.getContext('2d');

    // Очищаем canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    try {
      // Рисуем базовое изображение с учётом инверсии (если включена)
      ctx.save();
      if (invertColors) {
        try {
          ctx.filter = 'invert(1)';
        } catch (e) {
          // если filter не поддерживается, можно реализовать пиксельную инверсию позже
        }
      }
      ctx.drawImage(
        imgElement,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
      );
      ctx.restore();
      // Накладываем рисунок с overlay.
      // overlay содержит изображение в дисплейных пикселях, поэтому переводим окно обрезки из натуральных пикселей в дисплейные.
      const overlay = overlayCanvasRef.current;
      if (overlay) {
        try {
          const tmp = document.createElement('canvas');
          tmp.width = pixelCrop.width;
          tmp.height = pixelCrop.height;
          const tctx = tmp.getContext('2d');
          if (tctx) {
            const sx = Math.round(pixelCrop.x / overlayToNaturalScaleRef.current.sx);
            const sy = Math.round(pixelCrop.y / overlayToNaturalScaleRef.current.sy);
            const sWidth = Math.round(pixelCrop.width / overlayToNaturalScaleRef.current.sx);
            const sHeight = Math.round(pixelCrop.height / overlayToNaturalScaleRef.current.sy);
            tctx.drawImage(overlay, sx, sy, sWidth, sHeight, 0, 0, pixelCrop.width, pixelCrop.height);
            ctx.drawImage(tmp, 0, 0);
          }
        } catch (e) {
          // no-op
        }
      }
    } catch (error) {
      console.error('Error drawing image to canvas:', error);
      return null;
    }

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Не удалось создать Blob.'));
            return;
          }
          // console.log('Blob created successfully, size:', blob.size);
          resolve(blob);
        },
        'image/jpeg',
        0.95
      );
    });
  }, [imageLoaded, completedCrop, crop, getPixelCrop, invertColors]);

  // Оборачиваем handleConfirm в useCallback, чтобы зависимость для useEffect была стабильной
  const handleConfirm = useCallback(async () => {
    if (!imageLoaded) {
      console.error('Cannot crop: image not loaded');
      return;
    }

    try {
      const blob = await generateCroppedImage();
      if (blob) {
        onComplete(blob, completedCrop || crop);
      } else {
        console.log('Обрезанное изображение вернулось пустым.');
      }
    } catch (err) {
      console.error('Error during crop:', err);
    }
  }, [completedCrop, crop, onComplete, imageLoaded, generateCroppedImage]);

  // Глобальный обработчик клавиш – срабатывает вне зависимости от фокуса
  useEffect(() => {
    const keyListener = (e) => {
      if (e.key === 'Enter' && imageLoaded) {
        handleConfirm();
      }
    };
    window.addEventListener('keydown', keyListener);
    return () => window.removeEventListener('keydown', keyListener);
  }, [handleConfirm, imageLoaded]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div ref={containerRef} style={{ position: 'relative', display: 'inline-block' }}>
            <ReactCrop
              crop={crop}
              onImageLoaded={onImageLoaded}
              onChange={(newCrop) => setCrop(newCrop)}
              onComplete={(c) => setCompletedCrop(c)}
              style={{ position: 'relative' }}
              disabled={activeTool !== 'none'}
            >
              <img
              key={imgKey}
              src={displayedSrc}
                alt="Для обрезки"
              style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', display: 'block', filter: invertColors ? 'invert(1)' : 'none' }}
                onLoad={handleImageLoad}
              />
            </ReactCrop>
          {/* Canvas поверх изображения для рисования */}
              <canvas
            ref={overlayCanvasRef}
            style={{ position: 'absolute', left: 0, top: 0, zIndex: 2147483647, cursor: activeTool === 'eyedropper' ? 'crosshair' : activeTool === 'none' ? 'default' : 'none', pointerEvents: activeTool === 'none' ? 'none' : 'auto' }}
            onMouseEnter={handlePointerEnter}
            onMouseDown={handlePointerDown}
            onMouseMove={handlePointerMove}
            onMouseUp={handlePointerUp}
            onMouseLeave={handlePointerLeave}
          />
          {/* Круг курсора для показа размера кисти */}
          {showBrushCursor && (
            <div
              style={{
                position: 'absolute',
                left: cursorPos.x - brushSize / 2,
                top: cursorPos.y - brushSize / 2,
                width: brushSize,
                height: brushSize,
                borderRadius: '50%',
                border: `2px solid ${activeTool === 'eraser' ? '#ff4d4f' : brushColor}`,
                backgroundColor: activeTool === 'eraser' ? 'rgba(255, 77, 79, 0.2)' : 'rgba(0, 0, 0, 0.1)',
                pointerEvents: 'none',
                zIndex: 2147483648,
                transition: 'none',
              }}
            />
          )}
        </div>
        <div style={{ minWidth: 210 }}>
          <div style={{ marginBottom: 8, fontWeight: 600 }}>Инструменты</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <Tooltip title="Выделение">
                <Button
                  size="small"
                  shape="circle"
                  style={smallCircleBtnStyle}
                  type={activeTool === 'none' ? 'primary' : 'default'}
                  onClick={() => setActiveTool('none')}
                  icon={<ExpandOutlined style={smallIconStyle} />}
                />
              </Tooltip>
              <Tooltip title="Карандаш">
                <Button
                  size="small"
                  shape="circle"
                  style={smallCircleBtnStyle}
                  type={activeTool === 'pencil' ? 'primary' : 'default'}
                  onClick={() => setActiveTool('pencil')}
                  icon={<EditOutlined style={smallIconStyle} />}
                />
              </Tooltip>
              <Tooltip title="Ластик">
                <Button
                  size="small"
                  shape="circle"
                  style={smallCircleBtnStyle}
                  type={activeTool === 'eraser' ? 'primary' : 'default'}
                  onClick={() => setActiveTool('eraser')}
                  icon={<BorderOutlined style={smallIconStyle} />}
                />
              </Tooltip>
              <Tooltip title="Пипетка">
                <Button
                  size="small"
                  shape="circle"
                  style={smallCircleBtnStyle}
                  type={activeTool === 'eyedropper' ? 'primary' : 'default'}
                  onClick={() => setActiveTool('eyedropper')}
                  icon={<FormOutlined style={smallIconStyle} />}
                />
              </Tooltip>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label style={{ fontSize: 12 }}>Цвет:</label>
              <input type="color" value={brushColor} onChange={(e) => setBrushColor(e.target.value)} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label style={{ fontSize: 12 }}>Толщина:</label>
              <input type="range" min={1} max={50} value={brushSize} onChange={(e) => setBrushSize(Number(e.target.value))} />
              <span style={{ fontSize: 12, width: 24, textAlign: 'right' }}>{brushSize}</span>
                </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <Tooltip title="Очистить рисунок">
                <Button size="small" shape="circle" style={smallCircleBtnStyle} onClick={clearDrawing} icon={<CloseCircleOutlined style={smallIconStyle} />} />
              </Tooltip>
              <Tooltip title="Инвертировать цвета">
                <Button size="small" shape="circle" style={smallCircleBtnStyle} type={invertColors ? 'primary' : 'default'} onClick={() => setInvertColors(v => !v)} icon={<SwitcherOutlined style={smallIconStyle} />} />
              </Tooltip>
            </div>
            <div style={{ height: 1, background: 'var(--theme-divider)', margin: '4px 0' }} />
            <div style={{ display: 'flex', gap: 6 }}>
              <Tooltip title="Повернуть влево">
                <Button size="small" shape="circle" style={smallCircleBtnStyle} onClick={() => rotateDisplayedWithDrawing('left')} icon={<RotateLeftOutlined style={smallIconStyle} />} />
              </Tooltip>
              <Tooltip title="Повернуть вправо">
                <Button size="small" shape="circle" style={smallCircleBtnStyle} onClick={() => rotateDisplayedWithDrawing('right')} icon={<RotateRightOutlined style={smallIconStyle} />} />
              </Tooltip>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <Tooltip title="Зеркально отразить по горизонтали">
                <Button size="small" shape="circle" style={smallCircleBtnStyle} onClick={flipHorizontalWithDrawing} icon={<SwapOutlined style={smallIconStyle} />} />
              </Tooltip>
              <Tooltip title="Зеркально отразить по вертикали">
                <Button size="small" shape="circle" style={smallCircleBtnStyle} onClick={flipVerticalWithDrawing} icon={<ColumnHeightOutlined style={smallIconStyle} />} />
              </Tooltip>
            </div>
          </div>
        </div>
      </div>
      <div style={{ marginTop: 16, textAlign: 'center' }}>
        <Tooltip title="Обрезать">
          <Button 
            type="primary" 
            shape="circle"
            size="large"
            onClick={handleConfirm} 
            style={{ marginRight: 8, ...largeCircleBtnStyle }}
            disabled={!imageLoaded}
            icon={<CheckOutlined style={largeIconStyle} />}
          />
        </Tooltip>
        <Tooltip title="Отмена">
          <Button shape="circle" size="large" style={largeCircleBtnStyle} onClick={onCancel} icon={<CloseOutlined style={largeIconStyle} />} />
        </Tooltip>
      </div>
      {!imageLoaded && (
        <div style={{ textAlign: 'center', marginTop: 8, color: '#999' }}>
          Загрузка изображения...
        </div>
      )}
    </div>
  );
}

export default ImageCropper;
